import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import SaveIcon from "@material-ui/icons/Save";

import * as Api from "../../api/index";
import { ConnectedSheet } from "../sheet";
import { TableList, ConnectedTableList } from "./table-list";
import { Composer, ConnectedComposer } from "./table-layout-composer/composer";
import { SnackbarVariant } from "../../actions/store/type";
import { setSnackbarMessage } from "../../actions/store/snackbar-actions";
import { Table } from "../../api/tables";
import { objectMatches } from "../../util/object-utility";
import { Area } from "../../api/areas";

interface State {}

export interface AdminSettingsProps {}

interface ConnectedAdminSettingsProps extends WithStyles<typeof styles> {
	areas: Area[];
	tables: Table[];
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
}

type Props = AdminSettingsProps &
	ConnectedAdminSettingsProps &
	InjectedTranslateProps;

export class AdminSettings extends React.Component<Props, State> {
	private tableList: TableList;
	private tableComposers: Composer[] = [];

	private handleTableListRef = (instance: TableList) => {
		this.tableList = instance;
	};

	private handleTableComposerRef = (instance: Composer) => {
		this.tableComposers.push(instance);
	};

	private handleSaveClick = () => {
		const tables = this.tableList.getTables();
		const tableLayouts = this.tableComposers.reduce((acc, comp) => {
			return [
				...acc,
				...comp.getTableLayoutData(),
			];
		}, []);
		const savedTables = this.props.tables;

		const addedTables = tables.filter(table => table.id === null);

		const updatedTables = savedTables.reduce((acc: Table[], table) => {
			let ut = { ...table };
			let shouldUpdate = false;
			const nt = tables.find(t => t.id === table.id);
			const l = tableLayouts.filter(i => i.id === table.id)[0];

			try {
				if (objectMatches(table, nt) === false) {
					ut = nt;
					shouldUpdate = true;
				}
			} catch (e) {
				console.warn(e);
			}

			if (l && (l.x !== ut.layoutX || l.y !== ut.layoutY)) {
				ut = {
					...ut,
					layoutX: l.x,
					layoutY: l.y,
					layoutWidth: l.width,
					layoutHeight: l.height
				};

				shouldUpdate = true;
			}

			if (shouldUpdate) {
				acc.push(ut);
			}

			return acc;
		}, []);

		Promise.all([
			Api.addTables(addedTables),
			Api.updateTables(updatedTables)
		])
			.then(() => {
				this.props.setSnackbarMessage("Settings saved!", "success");
			})
			.catch(r => {
				console.error(r);
				this.props.setSnackbarMessage(
					"Could not save settings!",
					"error"
				);
			});
	};

	render() {
		const { classes, t, areas } = this.props;

		return (
			<ConnectedSheet maxWidth={1200}>
				<Typography variant="headline" component="h3">
					Admin-Einstellungen
				</Typography>

				<div className={classes.spacer} />
				<Typography
					className={classes.heading}
					variant="subheading"
					component="h4"
				>
					Verfügbare Tische
				</Typography>
				<Paper>
					<ConnectedTableList onRef={this.handleTableListRef} />
				</Paper>

				<div className={classes.spacer} />
				<Typography
					className={classes.heading}
					variant="subheading"
					component="h4"
				>
					Tisch-Layout
				</Typography>
				<Paper className={classes.paper}>
					{areas.map(area => (
						<ConnectedComposer
							onRef={this.handleTableComposerRef}
							areaId={area.id}
						/>
					))}
				</Paper>

				<Button
					variant="fab"
					color="primary"
					className={classes.saveButton}
					onClick={this.handleSaveClick}
				>
					<SaveIcon />
				</Button>
			</ConnectedSheet>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	tables: state.tables
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

const styles = (theme: Theme) =>
	createStyles({
		container: {},
		paper: {
			padding: 20
		},
		heading: {
			marginBottom: 10
		},
		saveButton: {
			position: "absolute",
			bottom: 20,
			right: 20
		},
		spacer: {
			marginTop: 25
		}
	});

export const ConnectedAdminSettings = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(AdminSettings)
	)
);
